import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import CountUp from "react-countup"
import {
  formatDisplayNumber,
  getDisplayCalories,
  getTrimmedDecimalPlaces,
} from "utils"

interface AnimatedValueProps {
  value: number
  loading: boolean
  duration?: number
  separator?: string
  suffix?: string
  emptyText?: string
  skipFirstTransition?: boolean
  disableAnimation?: boolean
  decimals?: number
}

const Wrapper = styled.span`
  display: inline-grid;
  > * {
    grid-area: 1 / 1;
  }
`

const Placeholder = styled.span<{ $show: boolean; $noTransition?: boolean }>`
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateX(${({ $show }) => ($show ? "0" : "-6px")});
  transition: ${({ $show, $noTransition }) =>
    $noTransition
      ? "none"
      : $show
      ? "opacity 0.2s ease-out, transform 0.2s ease-in"
      : "opacity 0.2s ease-out, transform 0.2s ease-out 0.05s"};
`

const ValueSpan = styled.span<{ $hidden: boolean; $noTransition?: boolean }>`
  display: inline-block;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transform: translateX(${({ $hidden }) => ($hidden ? "6px" : "0")});
  transition: ${({ $hidden, $noTransition }) =>
    $noTransition
      ? "none"
      : $hidden
      ? "opacity 0.2s ease-out 0.05s, transform 0.2s ease-out"
      : "opacity 0.2s ease-out, transform 0.2s ease-in"};
`

const AnimatedValue = ({
  value,
  loading,
  duration = 0.5,
  separator = ",",
  suffix,
  emptyText,
  skipFirstTransition = true,
  disableAnimation,
  decimals = 0,
}: AnimatedValueProps) => {
  const [shouldAnimate, setShouldAnimate] = useState(
    !skipFirstTransition && !loading,
  )
  const prevLoadingRef = useRef(loading)
  const hasLoadedOnce = useRef(!skipFirstTransition)

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setShouldAnimate(true)
      if (!hasLoadedOnce.current) {
        hasLoadedOnce.current = true
      }
    }
    prevLoadingRef.current = loading
  }, [loading])

  useEffect(() => {
    if (disableAnimation) {
      setShouldAnimate(false)
    }
  }, [disableAnimation])

  const noTransition = !hasLoadedOnce.current || disableAnimation
  const showEmptyText = !loading && value === 0 && emptyText !== undefined
  const displayDecimals = getTrimmedDecimalPlaces(value, decimals)

  return (
    <Wrapper>
      <Placeholder $show={loading} $noTransition={noTransition}>
        -
      </Placeholder>
      <ValueSpan $hidden={loading} $noTransition={noTransition}>
        {showEmptyText ? (
          emptyText
        ) : shouldAnimate && !disableAnimation ? (
          <>
            <CountUp
              decimals={displayDecimals}
              end={value}
              duration={duration}
              separator={separator}
              style={{ display: "inline-block" }}
              onEnd={() => setShouldAnimate(false)}
            />
            {suffix}
          </>
        ) : (
          <>
            {formatDisplayNumber(value, decimals)}
            {suffix}
          </>
        )}
      </ValueSpan>
    </Wrapper>
  )
}

export default AnimatedValue
